import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { History, Plus, Minus, ArrowRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { usePrivacy } from '../../contexts/PrivacyContext';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Calculate the difference with sign
const getDiff = (oldVal, newVal) => {
  if (oldVal === null || oldVal === undefined || newVal === null || newVal === undefined) return null;
  const diff = newVal - oldVal;
  return diff;
};

// Component to display a single value change
const ValueChange = ({ label, oldValue, newValue, isCurrency = false, isNew = false, isRemoved = false, formatCurrency }) => {
  const diff = isCurrency ? getDiff(oldValue, newValue) : null;
  const formatVal = isCurrency ? formatCurrency : (v) => v;
  
  if (isNew) {
    return (
      <div className="flex items-center justify-between py-1">
        <span className="text-xs text-slate-600">{label}</span>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-100 text-emerald-700 text-xs">(new)</Badge>
          <span className="text-xs font-medium text-emerald-600">{formatVal(newValue)}</span>
        </div>
      </div>
    );
  }
  
  if (isRemoved) {
    return (
      <div className="flex items-center justify-between py-1">
        <span className="text-xs text-slate-600 line-through">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-red-500 line-through">{formatVal(oldValue)}</span>
          <Badge className="bg-red-100 text-red-700 text-xs">Removed</Badge>
        </div>
      </div>
    );
  }
  
  // Show only if there's a change
  if (oldValue === newValue || (oldValue === undefined && newValue === undefined)) {
    return null;
  }
  
  return (
    <div className="flex items-center justify-between py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
      <div className="flex items-center gap-2">
        {oldValue !== undefined && oldValue !== null && (
          <span className="text-xs text-slate-400 line-through">{formatVal(oldValue)}</span>
        )}
        <ArrowRight className="w-3 h-3 text-slate-400" />
        <span className="text-xs font-medium text-slate-800 dark:text-slate-200">{formatVal(newValue)}</span>
        {diff !== null && diff !== 0 && (
          <Badge className={cn(
            "text-[10px] px-1.5 py-0",
            diff > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
          )}>
            {diff > 0 ? '+' : ''}{formatCurrency(diff)}
          </Badge>
        )}
      </div>
    </div>
  );
};

// Compare line items between old and new
const LineItemChanges = ({ oldItems = [], newItems = [], formatCurrency }) => {
  const oldMap = {};
  const newMap = {};
  
  (oldItems || []).forEach(item => {
    const key = item.description?.toLowerCase() || item.name?.toLowerCase() || '';
    oldMap[key] = item;
  });
  
  (newItems || []).forEach(item => {
    const key = item.description?.toLowerCase() || item.name?.toLowerCase() || '';
    newMap[key] = item;
  });
  
  const allKeys = new Set([...Object.keys(oldMap), ...Object.keys(newMap)]);
  const changes = [];
  
  allKeys.forEach(key => {
    const oldItem = oldMap[key];
    const newItem = newMap[key];
    
    if (!oldItem && newItem) {
      // New item added
      changes.push({
        label: newItem.description || newItem.name || 'Item',
        newValue: newItem.total_price || newItem.unit_price || newItem.price || 0,
        isNew: true,
        isCurrency: true
      });
    } else if (oldItem && !newItem) {
      // Item removed
      changes.push({
        label: oldItem.description || oldItem.name || 'Item',
        oldValue: oldItem.total_price || oldItem.unit_price || oldItem.price || 0,
        isRemoved: true,
        isCurrency: true
      });
    } else if (oldItem && newItem) {
      // Check for price change
      const oldPrice = oldItem.total_price || oldItem.unit_price || oldItem.price || 0;
      const newPrice = newItem.total_price || newItem.unit_price || newItem.price || 0;
      if (oldPrice !== newPrice) {
        changes.push({
          label: newItem.description || newItem.name || 'Item',
          oldValue: oldPrice,
          newValue: newPrice,
          isCurrency: true
        });
      }
    }
  });
  
  if (changes.length === 0) return null;
  
  return (
    <div className="mt-2 space-y-1">
      {changes.map((change, idx) => (
        <ValueChange
          key={idx}
          label={change.label}
          oldValue={change.oldValue}
          newValue={change.newValue}
          isCurrency={change.isCurrency}
          isNew={change.isNew}
          isRemoved={change.isRemoved}
          formatCurrency={formatCurrency}
        />
      ))}
    </div>
  );
};

const PricingHistory = ({ historyJson }) => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return formatMoney(amount);
  };
  
  if (!historyJson) return null;

  let history = [];
  try {
    history = typeof historyJson === 'string' ? JSON.parse(historyJson) : historyJson;
  } catch (e) {
    console.error("Failed to parse pricing history", e);
    return null;
  }

  if (!Array.isArray(history) || history.length === 0) return null;

  // Sort by date desc
  history.sort((a, b) => new Date(b.at || b.wef) - new Date(a.at || a.wef));

  return (
    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
          <History className="w-4 h-4" />
          Pricing Revision History ({history.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 relative pl-4 border-l border-slate-200 dark:border-slate-700">
          {history.map((entry, idx) => {
            const prevEntry = history[idx + 1]; // Previous revision (older)
            const hasChanges = entry.changes_summary || entry.old_values || entry.new_line_items;
            
            return (
              <div key={idx} className="relative">
                <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600 border-2 border-white dark:border-slate-800" />
                
                <div className="flex flex-col gap-1">
                  {/* Header with date */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                      {formatDate(entry.at)}
                    </span>
                    <div className="flex items-center gap-2">
                      {entry.wef && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          WEF: {formatDate(entry.wef).split(',')[0]}
                        </span>
                      )}
                      {entry.terminated && (
                        <Badge className="bg-red-100 text-red-700 text-xs">Terminated</Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Reason - Always show actual reason */}
                  <p className="text-xs text-slate-600 dark:text-slate-400 italic bg-slate-50 dark:bg-slate-900 p-2 rounded">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Reason:</span> {entry.reason || 'No reason provided'}
                  </p>

                  {/* Changes Details */}
                  {hasChanges && (
                    <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded text-xs mt-1 border border-slate-100 dark:border-slate-800">
                      <p className="font-semibold mb-2 text-slate-600 dark:text-slate-400 flex items-center gap-1">
                        <span>Changes</span>
                      </p>
                      
                      {/* Show old vs new values from changes_summary */}
                      {entry.changes_summary && (
                        <div className="space-y-1">
                          {/* Plan Amount Change */}
                          {entry.changes_summary.plan_amount !== undefined && (
                            <ValueChange
                              label="Plan Amount (Monthly)"
                              oldValue={entry.old_values?.plan_amount ?? prevEntry?.changes_summary?.plan_amount}
                              newValue={entry.changes_summary.plan_amount}
                              isCurrency={true}
                              formatCurrency={formatCurrency}
                            />
                          )}
                          
                          {/* One-time Charge Change */}
                          {entry.changes_summary.one_time_charge !== undefined && entry.changes_summary.one_time_charge !== null && (
                            <ValueChange
                              label="One-time Charge"
                              oldValue={entry.old_values?.one_time_charge ?? prevEntry?.changes_summary?.one_time_charge}
                              newValue={entry.changes_summary.one_time_charge}
                              isCurrency={true}
                              formatCurrency={formatCurrency}
                            />
                          )}
                          
                          {/* Total Users Change */}
                          {entry.changes_summary.total_users !== undefined && entry.changes_summary.total_users !== null && (
                            <ValueChange
                              label="Total Users"
                              oldValue={entry.old_values?.total_users ?? prevEntry?.changes_summary?.total_users}
                              newValue={entry.changes_summary.total_users}
                              isCurrency={false}
                              formatCurrency={formatCurrency}
                            />
                          )}
                          
                          {/* Extra User Rate Change */}
                          {entry.changes_summary.extra_user_rate !== undefined && (
                            <ValueChange
                              label="Extra User Rate"
                              oldValue={entry.old_values?.extra_user_rate ?? prevEntry?.changes_summary?.extra_user_rate}
                              newValue={entry.changes_summary.extra_user_rate}
                              isCurrency={true}
                              formatCurrency={formatCurrency}
                            />
                          )}
                          
                          {/* Base User Capacity */}
                          {entry.changes_summary.base_user_capacity !== undefined && (
                            <ValueChange
                              label="Base Users (Included)"
                              oldValue={entry.old_values?.base_user_capacity}
                              newValue={entry.changes_summary.base_user_capacity}
                              isCurrency={false}
                              formatCurrency={formatCurrency}
                            />
                          )}
                        </div>
                      )}

                      {/* Line Item Changes */}
                      {entry.new_line_items && (
                        <LineItemChanges 
                          oldItems={entry.old_line_items || prevEntry?.new_line_items} 
                          newItems={entry.new_line_items}
                          formatCurrency={formatCurrency}
                        />
                      )}
                      
                      {/* If no structured data, show raw summary */}
                      {!entry.changes_summary && !entry.new_line_items && entry.summary && (
                        <p className="text-xs text-slate-600">{entry.summary}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Cutoff Period if applicable */}
                  {entry.cutoff_period_month && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      Cutoff Period: {entry.cutoff_period_month}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingHistory;
